import classNames from "classnames";
import { useState, useRef, useMemo, useEffect, ReactNode } from "react";

import useOnClickOutside from "../../../hooks/useClickOutside";

import styles from "./index.module.scss";

interface ISelectProps {
  className?: string;
  containerClassName?: string;
  value: string;
  valueKey?: string;
  items: Array<{ value: string; label: string }>;
  label?: string;
  placeholder?: string;
  errorMessage?: ReactNode;
  successMessage?: ReactNode;
  isRequired?: boolean;
  disabled?: boolean;
  onChange: ({ value, valueKey }: { value: string; valueKey: string }) => void;
}

function CustomSelect({
  value,
  items,
  disabled,
  className = "",
  containerClassName = "",
  valueKey = "",
  label = "",
  placeholder = "",
  errorMessage,
  successMessage,
  isRequired = false,
  onChange = () => {},
}: ISelectProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [internalValue, setInternalValue] = useState(value ?? "");

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const activeOption = useMemo(
    () => items.find((el) => el.value === value),
    [value],
  );

  const outsideRef = useRef(null);

  const handleChange = (newValue: string) => () => {
    setIsVisible(false);
    setInternalValue(newValue);

    if (onChange) {
      onChange({ value: newValue, valueKey });
    }
  };

  useOnClickOutside(outsideRef, () => setIsVisible(false));

  return (
    <div
      className={classNames(styles.container, containerClassName, {
        [styles.container_disabled]: disabled,
      })}>
      {label && (
        <span className={styles.label}>
          {label}
          {isRequired && <span className={styles.asterisk}>*</span>}
        </span>
      )}

      <div ref={outsideRef} className={styles.fieldContainer}>
        <div
          className={classNames(styles.field, className, {
            [styles.field_error]: !!errorMessage,
            [styles.field_success]: !!successMessage,
          })}
          onClick={() => setIsVisible((prev) => !prev)}>
          <span
            className={classNames(styles.value, {
              [styles.placeholder]: !value,
              [styles.value_visible]: isVisible,
            })}>
            {activeOption?.label ?? placeholder}
          </span>
        </div>

        <div
          className={classNames(styles.items, {
            [styles.items_visible]: isVisible,
          })}>
          {items.map((item) => {
            const isChosen = internalValue === item.value;

            return (
              <div
                key={item.value}
                className={classNames(styles.item, {
                  [styles.item_chosen]: isChosen,
                })}
                onClick={handleChange(item.value)}>
                <span>{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {errorMessage ? (
        <span className={styles.error}>{errorMessage}</span>
      ) : successMessage ? (
        <span className={styles.success}>{successMessage}</span>
      ) : (
        <span className={styles.error} />
      )}
    </div>
  );
}

export default CustomSelect;
